// tslint:disable:no-trailing-whitespace
// tslint:disable:member-ordering
import { BaseEntity } from '../base-entity';
import { CancellationRequestDetail } from './cancellation-request-detail';
import { JobOrder } from './job-order';
import { JobOrderHistDetailAddon } from './job-order-hist-detail-addon';
import { JobOrderHistDetailFeature } from './job-order-hist-detail-feature';
import { JobOrderHistDetailReceipt } from './job-order-hist-detail-receipt';
import { Product } from './product';
import { ReturnPolicy } from './return-policy';
import { ReturnRequestDetail } from './return-request-detail';

/// <code-import> Place custom imports between <code-import> tags
import { DateFns } from '@utils';
import * as _ from 'lodash';
import { CancellationRequestDetailStatusEnum, ReturnRequestDetailStatusEnum } from './enums';
import { InvoiceDetailDTO } from './invoice-from-supplier-dto';
/// </code-import>

/// <module-code> Place module level code between <module-code> tags

/// </module-code>

export class JobOrderHistDetail extends BaseEntity  {

  /// <code> Place custom code between <code> tags
  invoiceDetails: InvoiceDetailDTO[] = [];

  // Expected ship dates from supplier (populated by GetJobOrderDetailStatuses, not persisted)
  supplierExpectedShipDate?: Date;
  supplierOrigExpectedShipDate?: Date;

  // Supplier-reported accepted-cancel qty (populated by GetJobOrderDetailStatuses, not persisted).
  // When present, preferred over summing local cancellationRequestDetails.
  supplierCancelQty?: number;

  getReturnRequestQty() {
    return _.sumBy(this.returnRequestDetails, 'qty');
  }

  getLastInvoiceShipDate(): Date {
    const maxInvoiceDetail = _.maxBy(this.invoiceDetails, (d) => d.invoiceHeader.shipTs);
    return maxInvoiceDetail?.invoiceHeader.shipTs ?? new Date();
  }

  getLastRepairDate(): Date | null {
    if (this.returnPolicy && this.returnPolicy.allowRepair && this.returnPolicy.repairDays && this.returnPolicy.repairDays > 0) {
      const lastInvoiceShipDate = this.getLastInvoiceShipDate();
      return DateFns.dateAdd(lastInvoiceShipDate, this.returnPolicy.repairDays, 'days');
    }
    return null;
  }

  getLastReturnDate(): Date | null {
    if (this.returnPolicy && this.returnPolicy.allowReturn && this.returnPolicy.returnDays && this.returnPolicy.returnDays > 0) {
      const lastInvoiceShipDate = this.getLastInvoiceShipDate();
      return DateFns.dateAdd(lastInvoiceShipDate, this.returnPolicy.returnDays, 'days');
    }
    return null;
  }

  isReturnable(isAdmin: boolean): boolean {
    if (isAdmin) {
      return this.returnPolicy && this.returnPolicy.allowReturn;
    }
    const today = new Date();
    const lastReturnDate = this.getLastReturnDate();
    if (!lastReturnDate) {
      return false;
    }
    return lastReturnDate > today && this.getReturnableOrRepairableQty() > 0;
  }

  isRepairable(isAdmin: boolean): boolean {
    if (isAdmin) {
      return this.returnPolicy && this.returnPolicy.allowRepair;
    }
    const today = new Date();
    const lastRepairDate = this.getLastRepairDate();
    if (!lastRepairDate) {
      return false;
    }
    return lastRepairDate > today && this.getReturnableOrRepairableQty() > 0;
  }

  isReturnOrRepairable(isAdmin: boolean): boolean {
    return this.isReturnable(isAdmin) || this.isRepairable(isAdmin);
  }

  getReturnableOrRepairableQty(): number {
    // check return window - this can be overridden by admin
    return this.getShippedQty() - this.getReturnedOrInProcessQty();
  }

  getCancellableQty() {
    return this.qty - (this.getShippedQty() + this.getReturnedQty() + this.getCancelledQty());
  }

  // requires that .invoiceDetails has already been populated - see
  getShippedQty() {
    var _shipped = 0;
    this.invoiceDetails.forEach((c) => {
      _shipped += c.shipQty + c.redistributeQty;
    });
    return _shipped;
  }

  getCancelledQty() {
    // Prefer supplier-reported scalar when present — it carries pre-migration cancels
    // that never produced a CancellationRequestDetail row.
    if (this.supplierCancelQty != null) {
      return this.supplierCancelQty;
    }
    var qty = 0;
    this.cancellationRequestDetails
      // Once its rejected - you can't try again - except by creating a new cancellation detail.
      .filter((x) => x.cancellationRequestDetailStatusId != CancellationRequestDetailStatusEnum.SupplierRejected)
      .forEach((x) => {
        qty += x.qty;
      });

    return qty;
  }

  getReturnedOrInProcessQty() {
    var qtys = this.returnRequestDetails
      .filter((x) => x.returnRequestDetailStatusId != ReturnRequestDetailStatusEnum.SupplierRejected)
      .map((x) => x.qty);
    return _.sum(qtys) ?? 0;
  }

  getReturnedQty() {
    var _returned = 0;
    this.returnRequestDetails.forEach((x) => {
      if (x.returnRequestDetailStatusId === ReturnRequestDetailStatusEnum.SupplierAccepted) {
        _returned += x.qty;
      }
    });
    return _returned;
  }

  getOpenQty() {
    return Math.max(0, this.qty - this.getCancelledQty() - this.getShippedQty() - this.getReturnedQty());
  }

  getUnitPrice() {
    return this.price + this.getFeaturesPrice() + this.getAddonsPrice();
  }

  getAddonsPrice() {
    return _.sumBy(this.jobOrderHistDetailAddons, (x) => x.price);
  }

  getFeaturesPrice() {
    return _.sumBy(this.jobOrderHistDetailFeatures, (x) => x.price);
  }

  // --- Allowance-aware price helpers -------------------------------------------------
  // A program allowance may exclude specific features (oversize) and addons (embroidery,
  // screen print) from what it charges. job-order-placement.service.ts stamps that decision
  // per line onto wasIncludedInAllowance at placement time, so these read the historical
  // truth rather than today's ProgramAllowance exception lists.
  //
  // The getters above are PER UNIT; the getExtended* variants below are x qty, which is the
  // grain ProgramAllowanceUserLog.Amt is computed at (see ProgramAllowanceWrapper.curAmt).

  /** Per-unit feature price the allowance did NOT charge for. */
  getExcludedFeaturesPrice() {
    return _.sumBy(
      this.jobOrderHistDetailFeatures.filter((x) => !x.wasIncludedInAllowance),
      (x) => x.price,
    );
  }

  /** Per-unit addon price the allowance did NOT charge for. */
  getExcludedAddonsPrice() {
    return _.sumBy(
      this.jobOrderHistDetailAddons.filter((x) => !x.wasIncludedInAllowance),
      (x) => x.price,
    );
  }

  /** Extended (x qty) gross line total - base + every feature and addon, excluded or not. */
  getExtendedLineTotal() {
    return this.getUnitPrice() * this.qty;
  }

  /** Extended (x qty) amount the allowance left off this line. */
  getExtendedExcludedAmt() {
    return (this.getExcludedFeaturesPrice() + this.getExcludedAddonsPrice()) * this.qty;
  }

  /** Extended (x qty) amount this line actually contributed to the allowance charge. */
  getExtendedAllowanceChargedAmt() {
    return this.getExtendedLineTotal() - this.getExtendedExcludedAmt();
  }

  get unitPrice() {
    return this.getUnitPrice();
  }

  get shippedQty() {
    return this.getShippedQty();
  }

  get orderedExtendAmt() {
    return this.unitPrice * this.qty;
  }
  /// </code>

  // Generated code. Do not place code below this line.
  /** Guid key */
  id!: string;
  /** Guid FK jobOrder */
  jobOrderId!: string;
  /** Decimal */
  price!: number;
  /** Guid FK product */
  productId!: string;
  /** String(60) */
  productTypeConfigName!: string;
  /** Int32 */
  qty!: number;
  /** Guid FK returnPolicy */
  returnPolicyId?: string;
  /** Binary */
  rowVersion!: any;
  /** String(2000) */
  supplierStatusExplanation?: string;
  /** Decimal */
  unitReissueAmt?: number;
  /** Inv FK jobOrderHistDetailId */
  cancellationRequestDetails!: CancellationRequestDetail[];
  /** FK jobOrderId */
  jobOrder!: JobOrder;
  /** Inv FK jobOrderHistDetailId */
  jobOrderHistDetailAddons!: JobOrderHistDetailAddon[];
  /** Inv FK jobOrderHistDetailId */
  jobOrderHistDetailFeatures!: JobOrderHistDetailFeature[];
  /** Inv FK jobOrderHistDetailId */
  jobOrderHistDetailReceipts!: JobOrderHistDetailReceipt[];
  /** FK productId */
  product!: Product;
  /** FK returnPolicyId */
  returnPolicy!: ReturnPolicy;
  /** Inv FK jobOrderHistDetailId */
  returnRequestDetails!: ReturnRequestDetail[];
}

